const base = location.origin;

const Code = ({ children }: { children: string }) => (
  <pre className="overflow-x-auto rounded-lg bg-ink p-3 font-mono text-[12px] leading-relaxed text-stone-200">{children}</pre>
);

export function EmbedGuide() {
  return (
    <article className="mx-auto max-w-3xl space-y-8 pt-8 leading-relaxed">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Bädda in bilder</h1>
        <p className="mt-1 text-soft">
          Varje publicerad bild har en fast länk. Kopiera koden från bildens sida (”Bädda in”) och klistra in den i ett HTML- eller
          kodblock på webbplatsen. Bilden levereras i rätt storlek och format för varje skärm, och ändringar i bildbanken slår igenom på sajten.
        </p>
      </header>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">1. Statisk HTML (rekommenderad)</h2>
        <p className="text-soft">
          Ren HTML med <code>&lt;picture&gt;</code>, AVIF/WebP/JPG, <code>srcset</code>, alt-text, bredd/höjd (ingen layoutförskjutning) och fotografbyline.
          Fungerar även där webbplatsen rensar bort skript. Byts filen eller fokuspunkten i bildbanken uppdateras bilden på sajten inom en timme.
          Alt-text och byline står i koden och ändras därför inte automatiskt.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">2. Webbkomponent (alltid aktuell)</h2>
        <p className="text-soft">Hämtar metadata live vid varje sidvisning. Bilden döljs automatiskt om den arkiveras eller licensen går ut.</p>
        <Code>{`<script type="module" src="${base}/embed.js"></script>
<bildbanken-bild asset="BILD-ID" ratio="16:9"></bildbanken-bild>`}</Code>
        <p className="text-[13px] text-soft">Attribut: <code>asset</code>, <code>ratio</code> (t.ex. 16:9, 1:1), <code>sizes</code>, <code>caption="false"</code>. Elementet skickar händelsen <code>bildbanken:load</code> med all metadata.</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Kan all metadata läsas via länk? Ja.</h2>
        <p className="text-soft">På tre sätt, så att den följer med oavsett hur bilden används:</p>
        <ol className="list-decimal space-y-2 pl-5 text-soft">
          <li><b className="text-ink">Metadatalänk</b> – <code>{base}/m/BILD-ID</code> ger JSON med titel, alt-text, fotograf, kreditering, licens och utgångsdatum, samtyckesstatus, taggar, plats, fokuspunkt, färg, srcset för alla format och ett färdigt schema.org-objekt. CORS är öppen, så att publiceringssystemet eller vilket skript som helst kan läsa den.</li>
          <li><b className="text-ink">HTTP-header på varje bild</b> – varje bildsvar har <code>Link: &lt;…/m/BILD-ID&gt;; rel="describedby"</code>, så att man kan gå från en bildlänk till dess metadata.</li>
          <li><b className="text-ink">Inbäddat i filen</b> – titel, alt-text, fotograf, rättigheter och licensens utgångsdatum skrivs in som XMP/IPTC i varje levererad fil. Krediteringen följer med även om någon sparar ner bilden.</li>
        </ol>
        <Code>{`curl ${base}/m/BILD-ID
curl -I "${base}/i/BILD-ID/namn.webp?w=800"   # → Link: <…/m/BILD-ID>; rel="describedby"`}</Code>
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Bildlänkar</h2>
        <Code>{`${base}/i/BILD-ID/valfritt-namn.{webp|avif|jpg}?w=1280&ratio=16:9`}</Code>
        <ul className="list-disc space-y-1 pl-5 text-soft">
          <li><code>w</code> – bredd. Avrundas till 320, 480, 640, 800, 1024, 1280, 1600, 1920 eller 2560 så att cachen hålls effektiv.</li>
          <li><code>ratio</code> – beskärning runt bildens fokuspunkt (t.ex. 16:9, 4:5, 1:1).</li>
          <li>Namnet i länken är bara för SEO. Bilden hittas på sitt id.</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Övriga gränssnitt</h2>
        <ul className="list-disc space-y-1 pl-5 text-soft">
          <li><code>{base}/oembed?url=…</code> – oEmbed för verktyg som stöder det.</li>
          <li><code>{base}/api/public/search?q=…</code> – sök bland publicerade bilder (underlag för en framtida bildväljare i publiceringssystemet).</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Skydd som är inbyggt</h2>
        <ul className="list-disc space-y-1 pl-5 text-soft">
          <li>Bara <b className="text-ink">publicerade</b> bilder går att nå utifrån. Utkast svarar 404 och arkiverade 410.</li>
          <li>Bilder med <b className="text-ink">utgången licens</b> eller <b className="text-ink">personer utan samtycke</b> slutar levereras automatiskt.</li>
          <li>Publicering kräver alt-text, angiven licens och bedömt samtycke.</li>
          <li>”Används på” registreras automatiskt när en sida laddar bilden, så du ser vilka sidor som påverkas innan du arkiverar en bild.</li>
        </ul>
      </section>
    </article>
  );
}
